import { Consent, ErrorResponse, ServiceResponse } from '@bzbs/react-api-client';
import React, { createContext, useContext, useState } from 'react';
import { useAnalyticsContext } from './analytics_provider';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';

type ConsentContextType = {
  consentData: Consent | null;
  isLoading: boolean;
  error: ErrorResponse | null;
  actions: {
    fetchConsent: () => Promise<ServiceResponse<Consent>>;
    updateConsent: (data: Consent) => Promise<ServiceResponse<Consent>>;
    unconsent: () => Promise<ServiceResponse<any>>;
  };
};

export const ConsentContext = createContext({
  consentData: null,
  isLoading: false,
  error: null,
  actions: {
    fetchConsent: async () => ({ type: 'client-error', message: 'Not implemented' }),
    updateConsent: async () => ({ type: 'client-error', message: 'Not implemented' }),
    unconsent: async () => ({ type: 'client-error', message: 'Not implemented' }),
  },
} as ConsentContextType);

export const useConsentContext = () => {
  return useContext(ConsentContext);
};

export const ConsentProvider = ({ children }: { children: React.ReactNode }) => {
  const [consentData, setConsentData] = useState<Consent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const { bzbsService } = useBuzzebeesServiceContext();
  const { actions: analyticsActions } = useAnalyticsContext();

  const fetchConsent = async (): Promise<ServiceResponse<Consent>> => {
    setIsLoading(true);
    const response = await bzbsService.consentApi.consent();
    setIsLoading(false);
    if (response.type === 'success') {
      setConsentData(response.model);
    } else {
      setError(response);
    }

    return response;
  };

  const updateConsent = async (data: Consent): Promise<ServiceResponse<Consent>> => {
    setIsLoading(true);
    const response = await bzbsService.consentApi.updateConsent({
      termsAndConditions: data.TermAndCondition?.toString(),
      dataPrivacy: data.DataPrivacy?.toString(),
      marketingOption: data.MarketingOption?.toString(),
      email: data.EmailMarketing?.toString(),
      sms: data.SMSMarketing?.toString(),
      notification: data.NotificationMarketing?.toString(),
      line: data.LineMarketing?.toString(),
      analyticsBuzzebeesCookies: data.AnalyticsBuzzebeesCookies?.toString(),
      analyticsFirebaseCookies: data.AnalyticsFirebaseCookies?.toString(),
      analyticsGoogleCookies: data.AnalyticsGoogleCookies?.toString(),
      analyticsMetaCookies: data.AnalyticsMetaCookies?.toString(),
      analyticsOtherCookies: data.AnalyticsOtherCookies?.toString(),
      functionalCookies: data.FunctionalCookies?.toString(),
      marketingCookies: data.MarketingCookies?.toString(),
      necessaryCookies: data.NecessaryCookies?.toString(),
    });
    setIsLoading(false);
    if (response.type === 'success') {
      setConsentData(response.model);

      if (
        response.model.EmailMarketing == 0 &&
        response.model.SMSMarketing == 0 &&
        response.model.NotificationMarketing == 0 &&
        response.model.LineMarketing == 0
      ) {
        analyticsActions.trackEvent({
          action: 'on_click',
          name: 'unconsent_success',
          category: 'marketing_unconcent',
        });
      }
    } else {
      setError(response);
    }

    return response;
  };

  const unconsent = async (): Promise<ServiceResponse<any>> => {
    setIsLoading(true);
    const response = await bzbsService.consentApi.unconsent();
    setIsLoading(false);
    if (response.type === 'success') {
      setConsentData(null);
    } else {
      setError(response);
    }
    return response;
  };

  return (
    <ConsentContext.Provider
      value={{
        consentData,
        isLoading,
        error,
        actions: {
          fetchConsent,
          updateConsent,
          unconsent,
        },
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};
